/**
 * risksMapping.js
 * Mapping between risk labels and technical identifiers
 * Also provides human-readable labels for display
 */

/**
 * Mapping from legacy labels to technical identifiers
 * Used for EPCI data migration (temporary until EPCI data uses identifiers)
 */
export const RISK_LABEL_TO_ID = {
  'Inondation': 'inondation',
  'Inondations': 'inondation',
  'Remontée de nappe': 'remonteeNappe',
  'Risques côtiers (submersion marine, tsunami)': 'risqueCotier',
  'Mouvements de terrain': 'mouvementTerrain',
  'Recul du trait de cote': 'reculTraitCote',
  'Retrait gonflement des argiles': 'retraitGonflementArgile',
  'Retrait-Gonflement des argiles': 'retraitGonflementArgile',
  'Avalanche': 'avalanche',
  'Feu de foret': 'feuForet',
  'Feu de forêt': 'feuForet',
  'Vent violent': 'cyclone',
  'Vents violents et risques cycloniques': 'cyclone',
  'Ilot de Chaleur Urbain': 'icu',
  'Ilot de chaleur urbain': 'icu',
  'Submersion et érosion côtière': 'submersionErosionCotiere',
};

/**
 * Mapping from technical identifiers to human-readable labels
 * Used as fallback when domain-specific label is not available in risksText
 */
export const RISK_ID_TO_LABEL = {
  inondation: 'Inondation',
  remonteeNappe: 'Remontée de nappe',
  risqueCotier: 'Risques côtiers',
  reculTraitCote: 'Recul du trait de côte',
  mouvementTerrain: 'Mouvements de terrain',
  retraitGonflementArgile: 'Retrait gonflement des argiles',
  avalanche: 'Avalanche',
  feuForet: 'Feu de forêt',
  cyclone: 'Vents violents et risques cycloniques',
  icu: 'Ilot de chaleur urbain',
  submersionErosionCotiere: 'Submersion et érosion côtière',
};

/**
 * Mapping from risk identifiers to related indicator keys
 * Used in Risks.jsx to determine which risk warnings to display
 */
export const RISK_TO_INDICATORS = {
  inondation: ['R1', 'R2', 'R7', 'R9'],
  remonteeNappe: ['G3'],
  risqueCotier: ['R3'],
  reculTraitCote: ['R3'],
  mouvementTerrain: [], // No specific indicator
  retraitGonflementArgile: ['R5'],
  feuForet: ['R4'],
  avalanche: ['T2', 'T3'],
  cyclone: ['R8'],
  icu: ['S1', 'S2', 'S3'],
  submersionErosionCotiere: ['R3'],
};

/**
 * Aliases for territory-suffixed risk identifiers found in JSONL data
 * Normalizes them to canonical identifiers recognized by RISK_ID_TO_LABEL and RISK_TO_INDICATORS
 */
export const RISK_ID_ALIASES = {
  Inondations: 'inondation',
  cycloneNC: 'cyclone',
  risqueCotierNC: 'risqueCotier',
  cycloneDOM: 'cyclone',
  feuForetDOM: 'feuForet',
};

/**
 * Normalize a risk identifier by resolving territory-specific aliases
 * @param {string} id - Risk identifier (possibly suffixed)
 * @returns {string} Canonical risk identifier
 */
export const normalizeRiskId = (id) => RISK_ID_ALIASES[id] || id;

/**
 * Convert a risk label to its technical identifier
 * @param {string} label - Risk label
 * @returns {string|null} Risk identifier or null if not found
 */
export const labelToId = (label) => RISK_LABEL_TO_ID[label] || null;

/**
 * Convert a risk identifier to its human-readable label
 * @param {string} id - Risk identifier
 * @returns {string} Risk label (returns id if no label found)
 */
export const idToLabel = (id) => RISK_ID_TO_LABEL[id] || id;

/**
 * Get the human-readable label for a risk, with optional domain override
 *
 * Looks up the domain-specific label from risksText if available,
 * then falls back to the default variant label, then to RISK_ID_TO_LABEL.
 *
 * @param {string} id - Risk identifier
 * @param {string|undefined} domaine - Entity domain (e.g., 'Nouvelle-Calédonie')
 * @param {Object} risksText - Generated risks text object (from texts.js)
 * @returns {string} Risk label
 */
export const getRiskLabel = (id, domaine, risksText) => {
  const riskVariants = risksText?.[id];
  if (riskVariants) {
    const domainVariant = domaine && riskVariants[domaine];
    if (domainVariant?.label) return domainVariant.label;

    const defaultVariant = riskVariants['default'];
    if (defaultVariant?.label) return defaultVariant.label;
  }
  return RISK_ID_TO_LABEL[id] || id;
};

/**
 * Get indicator keys associated with a risk identifier
 * @param {string} riskId - Risk identifier
 * @returns {Array<string>} Array of indicator keys
 */
export const getIndicatorsForRisk = (riskId) => RISK_TO_INDICATORS[riskId] || [];

/**
 * Check if a risk should be displayed for a given indicator
 * @param {string} riskId - Risk identifier
 * @param {string} indicatorKey - Indicator key (e.g., 'R1', 'G3')
 * @returns {boolean} True if risk should be displayed for this indicator
 */
export const shouldDisplayRisk = (riskId, indicatorKey) => {
  const indicators = getIndicatorsForRisk(riskId);
  return indicators.includes(indicatorKey);
};
